// features/automation/components/RunProgressBar.js

import React, { useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { Loader2, CheckCircle2, XCircle, ChevronUp, ChevronDown, X, CircleCheck, CircleX, MinusCircle, Ban, ExternalLink, Square } from 'lucide-react';
import { resolveRunCounts, resolveRunOutcome } from '../../../shared/utils/permutationStatus';
import '../styles/RunProgressBar.css';

const RunProgressBar = ({
  runState, isRunning, isCompleted, hasFailures, workerStatus,
  isDetailsOpen, onToggleDetails, onClose, onGoToResults,
  onCancel, isCancelling, cancelError, autoCloseIn, onKeepOpen,
}) => {
  const hasProgressData = runState?.testResults?.testSummary?.runProgressPercentage > 0 ||
    runState?.testResults?.permutations?.length > 0;
  const isQueuing = isRunning && workerStatus !== 'streaming' && !hasProgressData;
  const trackRef = useRef(null);
  const bubbleRef = useRef(null);
  const rafRef = useRef(null);
  /** Where the bar had got to while the run was still going. */
  const lastLivePctRef = useRef(null);

  useEffect(() => {
    if (!isRunning) return;
    const pct = runState?.testResults?.testSummary?.runProgressPercentage;
    if (typeof pct === 'number') lastLivePctRef.current = pct;
  }, [isRunning, runState]);

  // A new run starts from nothing, so the previous run's mark must not stick
  useEffect(() => {
    if (isRunning) return undefined;
    return () => { lastLivePctRef.current = null; };
  }, [isRunning]);

  // RAF loop — repositions the portal bubble every frame so it's always attached
  useEffect(() => {
    const tick = () => {
      if (trackRef.current && bubbleRef.current && runState) {
        const summary = runState.testResults.testSummary;
        const pct = summary?.cancelled === true && lastLivePctRef.current != null
          ? lastLivePctRef.current
          : summary.runProgressPercentage;
        const rect = trackRef.current.getBoundingClientRect();
        const left = rect.left + (rect.width * pct) / 100;
        const top = rect.top;
        bubbleRef.current.style.left = `${left}px`;
        bubbleRef.current.style.top = `${top}px`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [runState]);

  if (!runState?.testResults) return null;

  const { testSummary, permutations } = runState.testResults;
  // Counted by status — a SKIPPED or DISABLED permutation reports hasPassed: true
  // and must not land in the passed tally.
  const { total, executed, passed, failed, skipped, disabled } =
    resolveRunCounts(testSummary, permutations);

  // A cancelled run reports runStatus: COMPLETED, so "Done" would be a lie about
  // a run the user stopped. Whatever finished before the stop is still reported.
  const { isCancelled } = resolveRunOutcome(testSummary);

  // The engine reports 100% on a cancelled run — it finished tidying up, not
  // the test. Showing a full bar says the work completed. The last percentage
  // seen while it was still running is where it actually got to.
  const runProgressPercentage = isCancelled
    ? (lastLivePctRef.current ?? testSummary.runProgressPercentage)
    : testSummary.runProgressPercentage;

  // "2 passed, 0 failed" is misleading when perms were skipped or disabled — name them.
  const doneParts = [`${passed} passed`, `${failed} failed`];
  if (skipped > 0) doneParts.push(`${skipped} skipped`);
  if (disabled > 0) doneParts.push(`${disabled} disabled`);
  // A cancelled run's tally is noise — often "0 passed, 0 failed (of 1)" — and
  // it invites the reader to judge a run that never got to finish. Just say so.
  const doneLabel = isCancelled
    ? 'Cancelled'
    : `Done — ${doneParts.join(', ')}${total > executed ? ` (of ${total})` : ''}`;

  return (
    <div className="mock-run-progress__inner">
      {/* Status — left */}
      <div className="mock-run-progress__status">
        {isRunning ? (
          <Loader2 size={14} className="mock-run-progress__status-icon mock-run-progress__status-icon--spinning" />
        ) : isCancelled ? (
          <Ban size={14} className="mock-run-progress__status-icon mock-run-progress__status-icon--cancelled" />
        ) : hasFailures ? (
          <XCircle size={14} className="mock-run-progress__status-icon mock-run-progress__status-icon--failed" />
        ) : (
          <CheckCircle2 size={14} className="mock-run-progress__status-icon mock-run-progress__status-icon--completed" />
        )}
        <span>
          {isRunning
            ? isQueuing
              ? 'Queuing…'
              : total > 1
                ? `Running · Perm ${executed + (executed < total ? 1 : 0)} of ${total}`
                : 'Running…'
            : doneLabel
          }
        </span>
      </div>

      {/* Progress bar — center */}
      <div className="mock-run-progress__bar-wrap">
        <div className="mock-run-progress__track" ref={trackRef}>
          <div
            className="mock-run-progress__fill"
            style={{ width: `${runProgressPercentage}%` }}
          />
        </div>
      </div>

      {/* Floating percentage bubble — rendered as portal, positioned by RAF loop */}
      {ReactDOM.createPortal(
        <span
          ref={bubbleRef}
          className={`mock-run-progress__bubble${isCompleted ? (isCancelled ? ' mock-run-progress__bubble--cancelled' : hasFailures ? ' mock-run-progress__bubble--failed' : ' mock-run-progress__bubble--completed') : ''}`}
        >
          {runProgressPercentage}%
        </span>,
        document.body
      )}

      {/* Status counts — skipped / disabled only appear when there are any, so the
          common all-executed case stays a two-badge row.
          Hidden for a cancelled run: the tally is incomplete by definition and
          reads as a verdict on a run that never got to finish. */}
      {!isCancelled && (
      <div className="mock-run-progress__counts">
        <span className="mock-run-progress__badge mock-run-progress__badge--passed" title="Passed">
          <CircleCheck size={10} />
          {passed}
        </span>
        <span className="mock-run-progress__badge mock-run-progress__badge--failed" title="Failed">
          <CircleX size={10} />
          {failed}
        </span>
        {skipped > 0 && (
          <span className="mock-run-progress__badge mock-run-progress__badge--skipped" title="Skipped — every step was disabled, so nothing ran">
            <MinusCircle size={10} />
            {skipped}
          </span>
        )}
        {disabled > 0 && (
          <span className="mock-run-progress__badge mock-run-progress__badge--disabled" title="Disabled — turned off in the data source">
            <Ban size={10} />
            {disabled}
          </span>
        )}
      </div>
      )}

      {/* Actions — right */}
      <div className="mock-run-progress__actions">
        {/* A failed cancel is reported here rather than thrown at the page —
            the run is still going and the user needs to know that. */}
        {cancelError && (
          <span className="mock-run-progress__cancel-error" role="alert" title={cancelError}>
            {cancelError}
          </span>
        )}
        <button
          type="button"
          className={`mock-run-progress__view-btn${isDetailsOpen ? ' mock-run-progress__view-btn--active' : ''}`}
          onClick={onToggleDetails}
        >
          {isDetailsOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          <span>{isDetailsOpen ? 'Hide' : 'Details'}</span>
        </button>
        {/* Only while the run can still be stopped — it disappears the moment
            the run finishes, where Results and Close take over. */}
        {isRunning && !isCompleted && onCancel && (
          <button
            type="button"
            className="mock-run-progress__cancel-btn"
            onClick={onCancel}
            disabled={isCancelling}
            title={isCancelling ? 'Cancelling…' : 'Cancel this run'}
          >
            {isCancelling
              ? <><Loader2 size={11} className="mock-run-progress__cancel-spin" /><span>Cancelling…</span></>
              : <><Square size={10} /><span>Cancel</span></>}
          </button>
        )}
        {isCompleted && (
          <button
            type="button"
            className={`mock-run-progress__results-btn${hasFailures ? ' mock-run-progress__results-btn--failed' : ''}`}
            onClick={onGoToResults}
          >
            <ExternalLink size={11} />
            <span>Results</span>
          </button>
        )}
        {isCompleted && autoCloseIn != null && (
          <span className="mock-run-progress__autoclose" role="timer" aria-live="polite">
            Closing in {autoCloseIn}s
            <button type="button" className="mock-run-progress__keep-open" onClick={onKeepOpen}>
              Keep open
            </button>
          </span>
        )}
        {isCompleted && (
          <button
            type="button"
            className="mock-run-progress__close-btn"
            onClick={onClose}
            title="Close"
          >
            <X size={12} />
          </button>
        )}
      </div>
    </div>
  );
};

RunProgressBar.propTypes = {
  runState: PropTypes.object,
  isRunning: PropTypes.bool.isRequired,
  isCompleted: PropTypes.bool.isRequired,
  hasFailures: PropTypes.bool,
  workerStatus: PropTypes.string,
  isDetailsOpen: PropTypes.bool.isRequired,
  onToggleDetails: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onGoToResults: PropTypes.func.isRequired,
  /** Omitted when the run cannot be cancelled — the button is then not rendered. */
  onCancel: PropTypes.func,
  isCancelling: PropTypes.bool,
  cancelError: PropTypes.string,
  /** Seconds until the finished window closes itself; null when not counting. */
  autoCloseIn: PropTypes.number,
  onKeepOpen: PropTypes.func,
};

RunProgressBar.defaultProps = {
  runState: null,
  hasFailures: false,
  workerStatus: 'idle',
  onCancel: null,
  isCancelling: false,
  cancelError: '',
  autoCloseIn: null,
  onKeepOpen: null,
};

export default RunProgressBar;
